import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    Select, MenuItem, FormControl, InputLabel, Typography, Chip, IconButton,
    CircularProgress, Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useFirebase } from '../../../context/FirebaseContext';
import { getAlphaWaves, createAlphaWave, updateWaveStatus } from '../../../api/alpha';
import logger from '../../../utils/logger';

const WavesManagement = ({ onUpdate }) => {
    const { auth } = useFirebase();
    const [waves, setWaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingWave, setEditingWave] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        capacity: 100,
        status: 'draft'
    });

    const loadWaves = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await new Promise((resolve) => {
                getAlphaWaves(auth, resolve);
            });
            if (response.error) {
                setError(response.error);
            } else if (Array.isArray(response)) {
                setWaves(response);
            } else if (response.data) {
                setWaves(response.data);
            }
        } catch (err) {
            logger.error('Error loading waves:', err);
            setError('Failed to load waves');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        loadWaves();
    }, [loadWaves]);

    const handleOpenDialog = (wave = null) => {
        if (wave) {
            setEditingWave(wave);
            setFormData({
                name: wave.name,
                description: wave.description || '',
                capacity: wave.capacity,
                status: wave.status
            });
        } else {
            setEditingWave(null);
            setFormData({
                name: '',
                description: '',
                capacity: 100,
                status: 'draft'
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingWave(null);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);
            let response;
            if (editingWave) {
                // Update status only
                response = await new Promise((resolve) => {
                    updateWaveStatus(auth, resolve, editingWave.id, formData.status);
                });
            } else {
                // Create new wave
                response = await new Promise((resolve) => {
                    createAlphaWave(auth, resolve, formData);
                });
            }

            if (response?.error) {
                setError(response.error);
                return;
            }

            handleCloseDialog();
            await loadWaves();
            onUpdate?.();
        } catch (err) {
            logger.error('Error saving wave:', err);
            setError('Failed to save wave');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'success';
            case 'draft': return 'warning';
            case 'closed': return 'default';
            case 'full': return 'error';
            default: return 'default';
        }
    };

    const formatDate = (dateArray) => {
        if (!dateArray || !Array.isArray(dateArray)) return 'N/A';
        const [year, month, day, hour, minute] = dateArray;
        return new Date(year, month - 1, day, hour || 0, minute || 0).toLocaleDateString();
    };

    if (loading && waves.length === 0) {
        return (
            <Box className="alpha-loading">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="waves-management">
            <Box className="alpha-section-header">
                <Typography variant="h6">Waves</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    size="small"
                >
                    New Wave
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Capacity</TableCell>
                            <TableCell>Users</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {waves.map((wave) => (
                            <TableRow key={wave.id}>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="500">
                                        {wave.name}
                                    </Typography>
                                    {wave.description && (
                                        <Typography variant="caption" color="textSecondary">
                                            {wave.description}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={wave.status}
                                        color={getStatusColor(wave.status)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{wave.capacity || 'Unlimited'}</TableCell>
                                <TableCell>{wave.currentCount || 0}</TableCell>
                                <TableCell>{formatDate(wave.createdAt)}</TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenDialog(wave)}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {waves.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="textSecondary">
                                        No waves created yet
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingWave ? 'Edit Wave' : 'Create New Wave'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            fullWidth
                            disabled={!!editingWave}
                        />
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                            disabled={!!editingWave}
                        />
                        <TextField
                            label="Max Capacity"
                            type="number"
                            value={formData.capacity}
                            onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                            fullWidth
                            disabled={!!editingWave}
                            helperText="0 for unlimited"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                label="Status"
                            >
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="closed">Closed</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!formData.name}
                    >
                        {editingWave ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WavesManagement;
